<template>
    <div :class="['language-select', { 'is-opened': opened }]" :style="{ width }">
        <CustomSelect
            ref="languageSelectRes"
            :model-value="fValue"
            :options="filteredLanguages"
            :emit-prop="emitProp"
            :empty-text="emptyText"
            :with-filter="withFilter"
            :placeholder="placeholder"
            :width="width"
            :close-on-select="false"
            :form-value-text="formValueText"
            async
            @opened="onOpened"
            @closed="onClosed"
            @update:model-value="onSelected"
            @filter="onFiltered"
            @filterCleared="onFilterCleared"
        >
            <template #items="{ items }">
                <div class="language-select__list">
                    <div v-for="item in items" :key="item.slug" class="language-select__item">
                        <div
                            :class="[
                                'language-select__item-label',
                                { 'is-selected': item.slug === selectedSlug }
                            ]"
                            @click.stop.prevent="onSelected(item)"
                        >
                            {{ item.label }}
                            <button
                                class="language-select__expand"
                                @click.stop.prevent="onExpand(item)"
                            />
                        </div>
                        <ul v-if="item.slug === selectedSlug" class="language-select__item-childs">
                            <li
                                v-for="child in item.languages"
                                :key="`lang-child-${child.id}`"
                                @click.stop.prevent="onSelectChild(item.slug, child)"
                                v-text="child.label[$i18n.locale]"
                            />
                        </ul>
                    </div>
                </div>
            </template>
        </CustomSelect>
    </div>
</template>

<script>
import i18n from '@speechki-frontend/core/i18n/i18n';
import { CustomSelect } from '@speechki-frontend/shared/ui/forms';

function findLanguageLabel(target, languages) {
    let result = '';
    languages.forEach(base => {
        if (base.languages) {
            base.languages.forEach(lang => {
                if (lang.slug === target) {
                    result = lang.label.original || lang.label.eng;
                }
            });
        }
    });

    return result;
}

export default {
    name: 'LanguageSelect',
    components: {
        CustomSelect
    },
    emits: ['update:modelValue', 'opened', 'closed', 'select'],
    props: {
        modelValue: {
            type: String,
            default: ''
        },
        showOptions: {
            type: Boolean,
            default: true
        },
        emitProp: {
            type: String,
            default: 'slug'
        },
        emptyText: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default() {
                return i18n.global.t('misc.select');
            }
        },
        withFilter: {
            type: Boolean,
            default: true
        },
        languages: {
            type: Array,
            default: () => []
        },
        width: {
            type: [String, Number],
            default: 'auto'
        }
    },
    data() {
        return {
            opened: false,
            selectedSlug: undefined,
            formValueText: undefined,
            fValue: undefined,
            filteredLanguages: []
        };
    },
    watch: {
        languages: {
            immediate: true,
            handler(languages) {
                if (this.modelValue) {
                    this.fValue = this.modelValue;
                    this.formValueText = findLanguageLabel(this.modelValue, languages);
                }

                this.filteredLanguages = languages;
            }
        }
    },
    methods: {
        findLanguageLabel,
        onOpened() {
            this.opened = true;
            this.$emit('opened');
        },
        onClosed() {
            this.opened = false;
            this.$emit('closed');
        },
        onSelected(item) {
            this.$refs.languageSelectRes.opened = false;
            this.$emit('select', { slug: item.slug, item });
            this.fValue = item.slug;
            this.formValueText = item.label;
        },
        onSelectChild(slug, item) {
            const { locale } = this.$i18n;
            this.$refs.languageSelectRes.opened = false;
            this.$emit('select', { slug, item });
            this.fValue = slug;
            this.formValueText = item.label[locale];
        },
        onExpand({ slug }) {
            this.selectedSlug = this.selectedSlug === slug ? undefined : slug;
        },
        onFiltered(value) {
            let result = JSON.parse(JSON.stringify(this.languages.slice()));

            if (!value) {
                this.filteredLanguages = result;
                return;
            }

            const filterMatch = new RegExp(value.toLowerCase(), 'gi');
            const filteredResult = [];

            result = result.filter(item => {
                if (!('languages' in item)) return false;

                return item.languages.some(child => child.label.eng.match(filterMatch));
            });

            result.forEach(item => {
                const languages = item.languages.filter(child =>
                    child.label.eng.match(filterMatch)
                );
                item.languages = languages;
                if (languages.length) {
                    filteredResult.push(item);
                }
            });

            result = filteredResult;

            this.filteredLanguages = result;
        },
        onFilterCleared() {
            this.selectedSlug = undefined;
            this.filteredLanguages = this.languages.slice();
        }
    }
};
</script>

<style lang="scss" src="./LanguageSelect.scss"></style>
